/* Core & libraries */
import React, { useState, useRef } from "react";
import { Modal, Accordion } from "react-bootstrap";
import { useTheme } from "react-jss";
import { FormattedMessage } from "react-intl";
import { Animated } from "react-animated-css";

/* Helpers, constants, custom methods, internal libraries */
import { useHasPermission } from '../../hooks/useHasPermission';

/* Components */
import UCButton from "../../components/buttons";
import { ReactComponent as AALogo } from "../../resources/images/aa-logo-mobile.svg";
import { ReactComponent as ChatIcon } from "../../resources/images/chat-lep.svg";
import { ReactComponent as CloseIcon } from "../../resources/images/loyalty-close-button.svg";
import CreditRequestHistory from './creditRequestHistory';
import AtmBulkImport from './atmBulkImport';

/* Styles */
import styles from "./styles";
import generalStyles from "../../resources/styles/general";

const RetroModal = ({ showModal, setShowModal }) => {
  const theme = useTheme();
  const classes = styles({ theme });
  const general = generalStyles({ theme });
  const [section, setSection] = useState("initial");
  const { checkPermission } = useHasPermission();

  const faqData = [
    {
      id: 1,
      title: <FormattedMessage id="retro.faqTitle1" />,
      descriptions: [
        <FormattedMessage id="retro.faqDescA1" />,
        <FormattedMessage id="retro.faqDescB1" />,
        <FormattedMessage id="retro.faqDescC1" />,
        <FormattedMessage id="retro.faqDescD1" />,
        <FormattedMessage id="retro.faqDescF1" />,
      ],
    },
    {
      id: 2,
      title: <FormattedMessage id="retro.faqTitle2" />,
      descriptions: [
        <FormattedMessage id="retro.faqDescA2" />,
        <FormattedMessage id="retro.faqDescB2" />,
        <FormattedMessage id="retro.faqDescC2" />,
      ],
    },
    {
      id: 3,
      title: <FormattedMessage id="retro.faqTitle3" />,
      descriptions: [<FormattedMessage id="retro.faqDescA3" />],
    },
    {
      id: 4,
      title: <FormattedMessage id="retro.faqTitle4" />,
      descriptions: [<FormattedMessage id="retro.faqDescA4" />],
    },
    {
      id: 5,
      title: <FormattedMessage id="retro.faqTitle5" />,
      descriptions: [<FormattedMessage id="retro.faqDescA5" />],
    },
    {
      id: 6,
      title: <FormattedMessage id="retro.faqTitle6" />,
      descriptions: [<FormattedMessage id="retro.faqDescA6" />],
    },
    {
      id: 7,
      title: <FormattedMessage id="retro.faqTitle7" />,
      descriptions: [<FormattedMessage id="retro.faqDescA7" />],
    },
    {
      id: 8,
      title: <FormattedMessage id="retro.faqTitle8" />,
      descriptions: [<FormattedMessage id="retro.faqDescA8" />],
    },
  ];

  const handleCloseButtonClick = () => {
    setShowModal(false);
  };

  const onUploadCompleted = () => {
    setSection("grid");
  };

  const renderSection = () => {
    switch (section) {
      case 'initial':
        return (
          <div className={classes.bodyContainer}>
            <div className={classes.leftPanel}>
              <h2>
                <FormattedMessage id="retro.title" />
              </h2>
              <h5>
                <FormattedMessage id="retro.subtitle" />
              </h5>
              <div className={classes.leftPanelMissingMiles} >
                <h5>
                  <FormattedMessage id="retro.subtitle2" />
                </h5>
                <ul>
                  <li> <FormattedMessage id="retro.subtitle2.bullet1" /></li>
                  <li> <FormattedMessage id="retro.subtitle2.bullet2" /></li>
                  <li> <FormattedMessage id="retro.subtitle2.bullet3" /></li>
                  <li> <FormattedMessage id="retro.subtitle2.bullet4" /></li>
                </ul>
              </div>
              <div className={classes.buttonWrapper}>
                <UCButton type="default" onClick={() => setSection('grid')}>
                  <FormattedMessage id="retro.checkRequestBtn" />
                </UCButton>
                {
                  checkPermission("F_RETRO_UPLOADREQUESTS") &&
                  <UCButton type="primary" onClick={() => setSection('upload')}>
                    <FormattedMessage id="retro.submitNewRequestBtn" />
                  </UCButton>
                }
              </div>
            </div>
            <div className={classes.rightPanel}>
              <h1>
                <FormattedMessage id="loyaltyRedeem.faqTitle" />
              </h1>
              <div className={classes.faqWrapper}>
                <Accordion alwaysOpen flush className={classes.accordion}>
                  {faqData.map((item, i) => (
                    <React.Fragment key={item.id}>
                      <Accordion.Item eventKey={i}>
                        <Accordion.Header>{item.title}</Accordion.Header>
                        <Accordion.Body>
                          {item.descriptions.map((desc, idx) => (
                            <React.Fragment key={idx}>
                              {idx === 0 ? (
                                <>
                                  {desc}
                                  <br />
                                  <br />
                                </>
                              ) : (
                                <>
                                  • {desc}
                                  <br />
                                </>
                              )}
                            </React.Fragment>
                          ))}
                        </Accordion.Body>
                      </Accordion.Item>
                      {i !== faqData.length - 1 && <hr className={classes.divider} />}
                    </React.Fragment>
                  ))}
                </Accordion>
              </div>
              <div className={classes.chatWrapper}>
                <div>
                  <ChatIcon />
                </div>
                <div className={classes.chatText}>
                  <h6>
                    <FormattedMessage id="retro.chatTitle" />
                  </h6>
                  <p>
                    <FormattedMessage id="retro.chatSubtitle" />
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
      case 'upload':
        return (
          <Animated
            animationIn="fadeIn"
            animationOut="fadeOut"
            animationOutDuration={150}
            animationInDuration={150}
          >
            <AtmBulkImport
              onUploadCompleted={onUploadCompleted}
            />
          </Animated>
        );
      case 'grid':
        return (
          <Animated
            animationIn="fadeIn"
            animationOut="fadeOut"
            animationOutDuration={150}
            animationInDuration={150}
          >
            <CreditRequestHistory />
          </Animated>
        );
    }
  }

  return (
    <Modal
      show={showModal}
      onHide={() => setShowModal(false)}
      className={`${general.modal} ${classes.fullPageModal}`}
      onExited={() => setSection("initial")}
    >
      <Modal.Header>
        <div>
          <AALogo />
          <div className={classes.modalHeaderText}>
            <h6>
              <FormattedMessage id="retro.headerTitle" />
            </h6>
            <span>
              <FormattedMessage id="retro.headerSubtitle" />
            </span>
          </div>
        </div>
        <UCButton
          onClick={() => {
            handleCloseButtonClick(false);
          }}
          className={classes.closeBtn}
        >
          <CloseIcon className={classes.closeIcon}/>
        </UCButton>
      </Modal.Header>
      <Modal.Body>
        {renderSection()}
      </Modal.Body>
    </Modal>
  );
};

export default RetroModal;
